import { usePrevious } from "../../../../../common/hooks/usePrevious";
import { useBadgePrint } from "../../../../../../common/hooks/useBadgePrint/useBadgePrint";
import { useLogEvent } from "../../../../../../common/hooks/useLogEvent/useLogEvent";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { badgesSelector, eventSecretSelector } from "../../../../../../redux/eventEdition/selectors/eventEdition.selectors";
import { IPersonBadge, IProduct } from "../../../../../../common/types";
import { productsSelector } from "../../../../../../redux/magento/selectors/magento.selectors";
import { countryListSelector } from "../../../../../../redux/country/selectors/country.selectors";
import QRUtils from "../../utils/utils";
import { BADGE_PRINTED_AT, SNACKBAR_VARIANTS } from "../../../../../../common/constants";
import PrintSettingsCache from "../../../../../printSettings/cache/PrintSettingsCache";
import { PrintMethods } from "../../../../../../common/enums";
import { DownloadFileTypes, PAPER_SIZE } from "@reedexpo/mercury-badge";
import { BadgeBuilder } from "../../../../../../common/badges/builder/BadgeBuilder";
import isEqual from "lodash.isequal";
import sortBy from "lodash.sortby";
import GenerateBadgeUtils from "./utils/utils";
import { RootState } from "../../../../../../redux/store";
import { BadgeDownloadOptions } from "../../types";
import searchFormUtils from "../../../../../../components/search/details/form/utils/searchFormUtils";
import dayjs from "common/dayjs";
import { setEventEditionBadges } from "../../../../../../redux/eventEdition/slice/eventEditionSlice";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";

export type GenerateBadgeProps = {
  qrLoading: boolean;
  qrCodeContent: string;
  onDownloadClick: (skip?: boolean, options?: BadgeDownloadOptions) => Promise<void>;
};

export const useGenerateOdinBadge = (badgeInfo: IPersonBadge): GenerateBadgeProps => {
  const dispatch = useDispatch();
  const prevBadge = usePrevious(badgeInfo);
  const { printBadge } = useBadgePrint();
  const { logEvent } = useLogEvent();
  const { australiaRegions, usaRegions } = useSelector((state: RootState) => state.country);
  const { eventEdition } = useSelector((state: RootState) => state.eventEdition);
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation("common");
  const isQRBeingPrinted = useRef(false);

  const [qrCodeContent, setQrCodeContent] = useState<string>(badgeInfo.qrCodeContent);
  const [qrLoading, setQrLoading] = useState(false);

  const badges: IPersonBadge[] = useSelector(badgesSelector);
  const badgeSecret = useSelector(eventSecretSelector);
  const products = useSelector(productsSelector);
  const countries = useSelector(countryListSelector);

  const generateQrCode = () => {
    setQrLoading(true);
    const el = document.getElementById("qr-code") as HTMLCanvasElement;

    if (el) {
      const base64QrCode = el.toDataURL("image/jpg", 0.8);
      setQrLoading(false);
      return base64QrCode;
    }
    setTimeout(() => generateQrCode(), 500);
    return "";
  };

  const updateBadge = () => {
    const entitlementId = getUpdatedEntitlementId();
    const parsedQR = QRUtils.parseQRCodeString((prevBadge as IPersonBadge).qrCodeContent, prevBadge.customerType);

    if (GenerateBadgeUtils.areBadgesNotTheSame(prevBadge, badgeInfo, parsedQR?.ebs_product_code, entitlementId)) {
      const qrContent = QRUtils.getUpdatedQrContent(badgeInfo.qrCodeContent, badgeInfo, entitlementId, badgeSecret, true);
      setQrCodeContent(qrContent);

      let found = false;

      const updatedBadges = badges.map(badge => {
        if (badge.id === badgeInfo.id && badge.qrCodeContent !== qrContent) {
          const newBadge = { ...badge };
          found = true;
          newBadge.qrCodeContent = qrContent;
          return newBadge;
        }
        return badge;
      });
      if (found && !isEqual(sortBy(badges, "id"), sortBy(updatedBadges, "id"))) {
        dispatch(setEventEditionBadges(updatedBadges));
      }
    }
    generateQrCode();
  };

  const logBadgeDownloadEvent = async badge => {
    const { id, customerType, productCode, badgeVersion } = badge;
    const event = { badgeId: id, customerType, productCode, badgeVersion };
    logEvent(event);
  };

  const getUpdatedEntitlementId = () => {
    const product = products.find((product: IProduct) => product.sku === badgeInfo.productCode);
    return product?.rx_entitlement_id;
  };

  useEffect(() => {
    updateBadge();
  }, [badgeInfo, badges]);

  useEffect(() => {
    generateQrCode();
  }, [qrCodeContent]);

  useEffect(() => {
    updateBadge();
  }, []);

  const updateBadgeVersion = (badgeToUpdate: IPersonBadge) => {
    const entitlementId = getUpdatedEntitlementId();
    badgeToUpdate.badgeVersion = badgeToUpdate.badgeVersion ? badgeToUpdate.badgeVersion + 1 : 1;
    const qrContent = QRUtils.getUpdatedQrContent(badgeToUpdate.qrCodeContent, badgeToUpdate, entitlementId, badgeSecret, false);
    setQrCodeContent(qrContent);
    badgeToUpdate.qrCodeContent = qrContent;
    const newBadges = searchFormUtils.getBadgesWithReplacedItem(badges, badgeToUpdate);
    dispatch(setEventEditionBadges(newBadges));

    generateQrCode();
  };

  const generateBadge = async (badgeToPrint: IPersonBadge, product: IProduct, options: BadgeDownloadOptions = {}) => {
    const qrCodeToPrint = generateQrCode();

    if (!badgeToPrint.lastPrintedDate) {
      badgeToPrint.lastPrintedDate = dayjs(new Date()).format();
      const newBadges = searchFormUtils.getBadgesWithReplacedItem(badges, badgeToPrint);
      dispatch(setEventEditionBadges(newBadges));
    }

    setQrLoading(true);
    const cachedSettings = PrintSettingsCache.get();
    const fileFormat =
      cachedSettings.printMethod === PrintMethods.PRINT_DIRECT ? DownloadFileTypes.PDF : cachedSettings.settingsValues.printFileFormat;

    try {
      const badge = await new BadgeBuilder(badgeToPrint).setCountryList(countries).generateBadge({
        paperSize: cachedSettings.printFormat as PAPER_SIZE,
        type: fileFormat,
        product,
        qrCode: qrCodeToPrint,
        regions: [...australiaRegions, ...usaRegions],
        eventLocale: eventEdition!.primaryLocale!,
        businessUnitCode: eventEdition!.businessUnitGbsCode,
        options
      });
      if (!isQRBeingPrinted.current) {
        isQRBeingPrinted.current = true;
        await printBadge(badge, options, cachedSettings.printFormat as PAPER_SIZE);
        setTimeout(() => {
          isQRBeingPrinted.current = false;
        }, 250);
      }

      setQrLoading(false);
    } catch (e) {
      console.error("Error while generating the badge: ", e);
      // There is an error on Safari when the badge is not generated, so we need to retry ("index out of range")
      if (!options.isRetry) {
        generateBadge(badgeToPrint, product, { ...options, isRetry: true });
      }
    }
  };

  const onDownloadClick = async (skip = false, options: BadgeDownloadOptions = {}) => {
    if (isQRBeingPrinted.current) {
      return;
    }
    if (!badgeSecret) {
      enqueueSnackbar(
        React.createElement(
          "div",
          { className: "twolines" },
          React.createElement("b", null, t("common:errors.requestError")),
          t("common:errors.refreshThePage")
        ),
        {
          ...SNACKBAR_VARIANTS.error,
          autoHideDuration: 20000
        }
      );
      return;
    }
    const badgeToPrint = options.providedBadge || badgeInfo;
    const product = products.find(p => p.sku === badgeToPrint.productCode) as IProduct;

    if (!product) {
      enqueueSnackbar(`Product not found.`, SNACKBAR_VARIANTS.error);
      isQRBeingPrinted.current = false;
      return;
    }

    if (!product.rx_entitlement_id) {
      enqueueSnackbar(`Malformed QR Code - No entitlement ID is present.`, SNACKBAR_VARIANTS.error);
      isQRBeingPrinted.current = false;
      return;
    }

    const isPrintAtShowPlus = product.rx_print_at === BADGE_PRINTED_AT.PRINTED_AT_SHOW_PLUS;

    if (isPrintAtShowPlus && badgeToPrint.lastPrintedDate) {
      updateBadgeVersion(badgeToPrint);
    }

    logBadgeDownloadEvent(badgeToPrint);

    try {
      setTimeout(async () => generateBadge(badgeToPrint, product, options), 1000);
    } catch (e) {
      console.log(e);
      if (!skip) {
        onDownloadClick(true);
      }
      setQrLoading(false);
    }
  };

  return { qrLoading, qrCodeContent, onDownloadClick };
};
